import { useEffect, useMemo, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnOrderState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import classNames from 'classnames';
import styles from './DataTable.module.scss';

interface DataTableProps<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
  /** Key under which sort and column choices are remembered in this browser. */
  storageKey: string;
  globalFilter?: string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
}

interface StoredPrefs {
  sorting?: SortingState;
  visibility?: VisibilityState;
  order?: ColumnOrderState;
}

const readPrefs = (key: string): StoredPrefs => {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    return raw ? (JSON.parse(raw) as StoredPrefs) : {};
  } catch {
    return {};
  }
};

const writePrefs = (key: string, prefs: StoredPrefs) => {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(prefs));
  } catch {
    // storage unavailable
  }
};

/**
 * Sortable, searchable table with a column chooser. Sort and visible columns are remembered per browser; rows
 * animate in when the data or order changes.
 */
export const DataTable = <T,>({
  columns,
  data,
  storageKey,
  globalFilter = '',
  onRowClick,
  emptyMessage = 'Nothing to show.',
}: DataTableProps<T>) => {
  const initial = useMemo(() => readPrefs(storageKey), [storageKey]);
  const [sorting, setSorting] = useState<SortingState>(initial.sorting ?? []);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initial.visibility ?? {},
  );
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(initial.order ?? []);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  useEffect(
    () => writePrefs(storageKey, { sorting, visibility: columnVisibility, order: columnOrder }),
    [storageKey, sorting, columnVisibility, columnOrder],
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility, columnOrder, globalFilter },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
  });

  /** Move the dragged column so it lands where it was dropped. Works on the full leaf order, hidden columns included. */
  const moveColumn = (from: string, to: string) => {
    if (from === to) {
      return;
    }
    const current =
      columnOrder.length > 0 ? columnOrder : table.getAllLeafColumns().map((column) => column.id);
    const next = current.filter((id) => id !== from);
    next.splice(next.indexOf(to), 0, from);
    setColumnOrder(next);
  };

  const orderKey = table
    .getRowModel()
    .rows.map((row) => row.id)
    .join('|');

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <span className={styles.count}>
          {table.getRowModel().rows.length} of {data.length}
        </span>
        <div className={styles.chooserWrap}>
          <button
            type="button"
            className={styles.ghost}
            onClick={() => setChooserOpen((open) => !open)}
            aria-expanded={chooserOpen}
          >
            Columns
          </button>
          {chooserOpen ? (
            <ul className={styles.chooser} role="menu" aria-label="Choose columns">
              <li>
                <button
                  type="button"
                  className={styles.chooserReset}
                  onClick={() => {
                    setColumnOrder([]);
                    setColumnVisibility({});
                    setSorting([]);
                  }}
                >
                  Reset columns
                </button>
              </li>
              {table.getAllLeafColumns().map((column) => (
                <li key={column.id}>
                  <label className={styles.chooserItem}>
                    <input
                      type="checkbox"
                      checked={column.getIsVisible()}
                      onChange={column.getToggleVisibilityHandler()}
                    />
                    {String(column.columnDef.header ?? column.id)}
                  </label>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
      <div className={styles.scroller}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sorted = header.column.getIsSorted();
                  const align = (header.column.columnDef.meta as { align?: 'right' } | undefined)
                    ?.align;
                  return (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className={classNames(styles.th, {
                        [styles.right]: align === 'right',
                        [styles.sortable]: header.column.getCanSort(),
                        [styles.dragging]: dragging === header.column.id,
                        [styles.dropBefore]:
                          dropTarget === header.column.id && dragging !== header.column.id,
                      })}
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', header.column.id);
                        setDragging(header.column.id);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        if (dropTarget !== header.column.id) {
                          setDropTarget(header.column.id);
                        }
                      }}
                      onDragLeave={() =>
                        setDropTarget((current) => (current === header.column.id ? null : current))
                      }
                      onDrop={(event) => {
                        event.preventDefault();
                        const from = event.dataTransfer.getData('text/plain') || dragging;
                        if (from) {
                          moveColumn(from, header.column.id);
                        }
                        setDragging(null);
                        setDropTarget(null);
                      }}
                      onDragEnd={() => {
                        setDragging(null);
                        setDropTarget(null);
                      }}
                      title="Drag to reorder"
                      aria-sort={
                        sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'
                      }
                    >
                      <button
                        type="button"
                        className={styles.thButton}
                        onClick={header.column.getToggleSortingHandler()}
                        disabled={!header.column.getCanSort()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span
                          className={classNames(styles.sortIcon, { [styles.sortActive]: sorted })}
                          aria-hidden="true"
                        >
                          {sorted === 'desc' ? '↓' : '↑'}
                        </span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody key={orderKey}>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td className={styles.empty} colSpan={table.getVisibleLeafColumns().length}>
                  {emptyMessage}
                </td>
              </tr>
            ) : null}
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.id}
                className={classNames(styles.tr, { [styles.clickable]: Boolean(onRowClick) })}
                style={{ animationDelay: `${Math.min(index, 12) * 35}ms` }}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => (event.key === 'Enter' ? onRowClick(row.original) : undefined)
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
              >
                {row.getVisibleCells().map((cell) => {
                  const align = (cell.column.columnDef.meta as { align?: 'right' } | undefined)
                    ?.align;
                  return (
                    <td
                      key={cell.id}
                      className={classNames(styles.td, { [styles.right]: align === 'right' })}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
